/**
 * Virtual categories are synthetic categories that do not exist as Category rows.
 * They are rendered client-side in the category sidebar (gated by a supplier flag)
 * and filter catalog items by a boolean item flag instead of category_id.
 */

export enum VirtualCategoryGuidEnum {
    NEW_RELEASES = 'NEW_RELEASES',
    READY_TO_SHIP = 'READY_TO_SHIP',
}

// Legacy numeric categoryId sentinels used by the typeahead/elasticsearch paths
export enum VirtualCategorySentinelIdEnum {
    NEW_RELEASES = 0,
    READY_TO_SHIP = -1,
}

export interface IVirtualCategory {
    guid: VirtualCategoryGuidEnum;
    sentinelId: VirtualCategorySentinelIdEnum;
    itemFlag: 'is_new_release' | 'is_ready_to_ship';
    supplierGate: 'has_new_releases_category' | 'has_ready_to_ship_category';
}

export const VIRTUAL_CATEGORIES: Record<VirtualCategoryGuidEnum, IVirtualCategory> = {
    [VirtualCategoryGuidEnum.NEW_RELEASES]: {
        guid: VirtualCategoryGuidEnum.NEW_RELEASES,
        sentinelId: VirtualCategorySentinelIdEnum.NEW_RELEASES,
        itemFlag: 'is_new_release',
        supplierGate: 'has_new_releases_category',
    },
    [VirtualCategoryGuidEnum.READY_TO_SHIP]: {
        guid: VirtualCategoryGuidEnum.READY_TO_SHIP,
        sentinelId: VirtualCategorySentinelIdEnum.READY_TO_SHIP,
        itemFlag: 'is_ready_to_ship',
        supplierGate: 'has_ready_to_ship_category',
    },
};

/**
 * Resolves a virtual category from a raw categoryGuid off the wire.
 * Real category guids return undefined.
 */
export const getVirtualCategoryByGuid = (guid?: string): IVirtualCategory | undefined => {
    if (!guid) {
        return undefined;
    }
    return Object.values(VIRTUAL_CATEGORIES).find((virtualCategory) => virtualCategory.guid === guid);
};

/**
 * Resolves a virtual category from a numeric categoryId sentinel.
 * NEW_RELEASES uses sentinel 0, so undefined/null must be checked explicitly — not falsiness.
 */
export const getVirtualCategoryBySentinelId = (id?: number): IVirtualCategory | undefined => {
    if (id === undefined || id === null) {
        return undefined;
    }
    return Object.values(VIRTUAL_CATEGORIES).find((virtualCategory) => virtualCategory.sentinelId === id);
};
